/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { MouseEvent, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import CloseIcon from '@mui/icons-material/Close';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { FhirPredefinedWidget, PageFhirType } from '../algorithm/types';
import { fetchPredefinedWidgetsThunk } from '../Fhir/fhirSlice';
import { RenderedHtmlWithPopup } from '../chat/components/RenderedHtml/RenderedHtmlWithPopup';
import chatStyles from '../chat/Message.module.scss';
import { contextualizeDynamedHtml, DYNAMED_TAB_LABEL, DynamedSection } from './dynamed';
import styles from './DynamedTabContentPage.module.scss';
import { SectionNavigation } from './SectionNavigation';

const DYNAMED_RECOMMENDATION_TOOLTIP_BY_LABEL: Record<string, string> = {
  'Conditional recommendation':
    '<strong>A conditional recommendation</strong> from DynaMed signifies that, while it may be appropriate for many patients, there is less confidence about the course of action one should typically take. There may be limitations in the evidence, a close balance between benefits and harm, and/or a range of values and preferences that would lead patients to make different choices.',
  'Strong recommendation':
    '<strong>A strong recommendation</strong> from DynaMed signifies that there is a high degree of confidence about the course of action one should typically take.',
};

export type DynamedContentProps = {
  allowEditing?: boolean;
  documentId?: string;
  documentSlug?: string;
  documentVersionId?: string;
  initialAnchor?: string;
  label?: string;
  onArticleLinkClick?: (slug: string) => void;
  pageId?: string;
  sections: DynamedSection[];
  transformArticleLink?: (slug: string) => string;
};

type DynamedTable = {
  content: string;
  id: string;
  title: string;
};

function buildGeneralReferenceLookup(sections: DynamedSection[]): Map<string, HTMLLIElement> {
  const references = new Map<string, HTMLLIElement>();
  sections.forEach((section) => {
    const document = new DOMParser().parseFromString(section.content, 'text/html');
    document.querySelectorAll<HTMLLIElement>('.GR_List li[id]').forEach((reference) => {
      references.set(reference.id, reference);
    });
  });
  return references;
}

function transformDynamedContent(
  content: string,
  transformArticleLink?: (slug: string) => string,
  generalReferences = new Map<string, HTMLLIElement>(),
  extractStandaloneTables = true
): { content: string; tables: DynamedTable[] } {
  if (
    !transformArticleLink &&
    !content.includes('data-transform="table"') &&
    !content.includes('data-transform="full-text-container"') &&
    generalReferences.size === 0
  )
    return { content, tables: [] };

  const document = new DOMParser().parseFromString(content, 'text/html');
  if (transformArticleLink) {
    document.querySelectorAll<HTMLAnchorElement>('a[data-type="article"][data-location="internal"]').forEach((link) => {
      const slug = link.getAttribute('href');
      if (slug) {
        link.setAttribute('data-dynamed-slug', slug);
        link.setAttribute('href', transformArticleLink(slug));
      }
    });
  }

  document.querySelectorAll<HTMLElement>('sup.ph.sup').forEach((citation) => {
    const references = [...citation.querySelectorAll<HTMLAnchorElement>('a')]
      .map((link) => {
        const href = link.getAttribute('href') || '';
        const referenceId = link.dataset.referenceId || href.slice(href.lastIndexOf('#') + 1);
        return generalReferences.get(referenceId);
      })
      .filter((reference): reference is HTMLLIElement => !!reference);
    if (references.length === 0) return;

    const action = document.createElement('action');
    action.setAttribute('type', 'reference');
    const actionContent = document.createElement('actioncontent');
    actionContent.setAttribute('type', 'reference');
    references.forEach((reference, index) => {
      if (index > 0) actionContent.append(document.createElement('br'));
      const item = document.createElement('span');
      item.className = 'dynamed-reference-entry';
      const clonedReference = reference.cloneNode(true) as HTMLLIElement;
      item.append(...Array.from(clonedReference.childNodes));
      actionContent.append(item);
    });
    action.append(actionContent);
    citation.replaceWith(action);
  });

  document.querySelectorAll<HTMLElement>('[data-transform="full-text-container"]').forEach((fullTextContainer) => {
    const reference = document.createElement('span');
    const action = document.createElement('action');
    action.setAttribute('type', 'reference');
    const actionContent = document.createElement('actioncontent');
    actionContent.setAttribute('type', 'reference');
    actionContent.append(fullTextContainer.dataset.title || '');
    if (fullTextContainer.closest('.dynamed-reference-entry')) {
      fullTextContainer.querySelectorAll<HTMLAnchorElement>('a').forEach((link) => {
        actionContent.append(' ', link);
      });
    } else {
      const links = document.createElement('ul');
      fullTextContainer.querySelectorAll<HTMLAnchorElement>('a').forEach((link) => {
        const item = document.createElement('li');
        item.append(link);
        links.append(item);
      });
      actionContent.append(links);
    }
    action.append(actionContent);
    reference.append(action);
    fullTextContainer.replaceWith(reference);
  });

  const tables = extractStandaloneTables
    ? [...document.querySelectorAll<HTMLElement>('[data-transform="table"][data-is-standalone="true"]')].map(
        (table, index) => {
          const id = table.dataset.id || `dynamed-table-${index}`;
          const title = table.dataset.title || `Table ${index + 1}`;
          const content = table.outerHTML;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'dynamed-table-launch';
          button.dataset.dynamedTableId = id;
          button.setAttribute('aria-label', `Open ${title}`);
          const icon = document.createElement('span');
          icon.className = 'dynamed-table-launch-icon';
          icon.ariaHidden = 'true';
          icon.textContent = '▦';
          const heading = document.createElement('span');
          heading.textContent = title;
          button.append(icon, heading);
          table.replaceWith(button);
          return { content, id, title };
        }
      )
    : [];

  return { content: document.body.innerHTML, tables };
}

export function buildDynamedDocumentHtml(
  sections: DynamedSection[],
  predefinedWidgets: FhirPredefinedWidget[]
): string {
  const generalReferences = buildGeneralReferenceLookup(sections);
  return sections
    .map((section) => {
      const heading = document.createElement('h2');
      heading.textContent = section.name;
      const transformedContent = transformDynamedContent(
        contextualizeDynamedHtml(section.content, predefinedWidgets, { pageName: section.name }),
        (slug) => new URL(slug, 'https://www.dynamed.com').toString(),
        generalReferences,
        false
      );
      return `${heading.outerHTML}${transformedContent.content}`;
    })
    .join('');
}

export function buildDynamedPageFhir(content: string): PageFhirType {
  const document = new DOMParser().parseFromString(content, 'text/html');
  const idsToFhir: PageFhirType['idsToFhir'] = {};

  document.querySelectorAll<HTMLElement>('fhir[id]').forEach((node) => {
    const id = node.getAttribute('id')?.trim();
    if (!id) return;

    const predefinedWidgetIds = new Set([
      ...(idsToFhir[id]?.predefinedWidgets.map((widget) => widget.id) || []),
      ...(node.getAttribute('data-fhir-widget-id') || '')
        .split(';')
        .map((widgetId) => widgetId.trim())
        .filter(Boolean),
    ]);
    idsToFhir[id] = {
      id,
      predefinedWidgets: [...predefinedWidgetIds].map((widgetId) => ({ id: widgetId })),
    };
  });

  return { idsToFhir };
}

function decodeJumpAnchor(href: string): string {
  const anchor = href.slice(1);
  try {
    return decodeURIComponent(anchor);
  } catch (error) {
    return anchor;
  }
}

export function DynamedContent({
  allowEditing,
  documentId,
  documentSlug,
  documentVersionId,
  initialAnchor,
  label = DYNAMED_TAB_LABEL,
  onArticleLinkClick,
  pageId,
  sections,
  transformArticleLink,
}: DynamedContentProps) {
  const dispatch = useAppDispatch();
  const predefinedWidgets = useAppSelector((state) => state.fhir.predefinedWidgets || []);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [pendingJumpTarget, setPendingJumpTarget] = useState<string | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const contentColumnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (predefinedWidgets.length === 0) {
      void dispatch(fetchPredefinedWidgetsThunk());
    }
  }, [dispatch, predefinedWidgets.length]);

  useEffect(() => {
    if (!initialAnchor) {
      setSelectedSectionIndex(0);
      return;
    }

    const sectionIndex = sections.findIndex((section) => {
      if (section.anchor === initialAnchor) return true;
      return !!new DOMParser().parseFromString(section.content, 'text/html').getElementById(initialAnchor);
    });
    setSelectedSectionIndex(Math.max(sectionIndex, 0));
    if (sectionIndex >= 0) setPendingJumpTarget(initialAnchor);
  }, [initialAnchor, sections]);

  useEffect(() => {
    if (!pendingJumpTarget) return;
    const target = [...(contentColumnRef.current?.querySelectorAll<HTMLElement>('[id]') || [])].find(
      (element) => element.id === pendingJumpTarget
    );
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingJumpTarget(null);
  }, [pendingJumpTarget, selectedSectionIndex]);

  const firstSection = sections[0];
  if (!firstSection) return <div className={styles.statusMessage}>No {label} content available.</div>;

  const currentSection = sections[selectedSectionIndex] ?? firstSection;
  const previousSection = selectedSectionIndex > 0 ? sections[selectedSectionIndex - 1] ?? null : null;
  const nextSection = selectedSectionIndex < sections.length - 1 ? sections[selectedSectionIndex + 1] ?? null : null;
  const transformedContent = transformDynamedContent(
    contextualizeDynamedHtml(currentSection.content, predefinedWidgets, { pageName: currentSection.name }),
    transformArticleLink,
    buildGeneralReferenceLookup(sections)
  );
  const selectedTable = transformedContent.tables.find((table) => table.id === selectedTableId) || null;

  function handleContentClick(event: MouseEvent<HTMLDivElement>) {
    const tableButton = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-dynamed-table-id]');
    if (tableButton?.dataset.dynamedTableId) {
      event.preventDefault();
      setSelectedTableId(tableButton.dataset.dynamedTableId);
      return;
    }

    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[data-location="internal"]');
    if (!link) return;

    if (link.dataset.type === 'jump') {
      const href = link.getAttribute('href');
      if (!href?.startsWith('#')) return;

      event.preventDefault();
      const anchor = decodeJumpAnchor(href);
      const sectionIndex = sections.findIndex((section) => {
        if (section.anchor === anchor) return true;
        return !!new DOMParser().parseFromString(section.content, 'text/html').getElementById(anchor);
      });
      if (sectionIndex >= 0) {
        setPendingJumpTarget(anchor);
        setSelectedSectionIndex(sectionIndex);
      }
      return;
    }

    if (link.dataset.type !== 'article' || !onArticleLinkClick) return;
    const slug = link.getAttribute('data-dynamed-slug') || link.getAttribute('href');
    if (!slug) return;

    event.preventDefault();
    onArticleLinkClick(slug);
  }

  return (
    <>
      <svg style={{ display: 'none' }}>
        <symbol
          className="sc-iGgWBj kmPCDl"
          viewBox="0 0 24 24"
          aria-hidden="true"
          role="presentation"
          focusable="false"
          id="external-pdf"
        >
          <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"></path>
        </symbol>
        <symbol
          className="sc-iGgWBj kmPCDl"
          viewBox="0 0 24 24"
          aria-hidden="true"
          role="presentation"
          focusable="false"
          id="external-launch"
        >
          <path d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"></path>
        </symbol>
      </svg>
      <div className={styles.dynamedLayout}>
        <aside className={styles.toc} aria-label={`${label} table of contents`}>
          <div className={styles.tocTitle}>Contents</div>
          <nav className={styles.tocList} aria-label={`${label} sections`}>
            {sections.map((section, index) => (
              <button
                key={`${section.name}-${index}`}
                aria-current={index === selectedSectionIndex ? 'page' : undefined}
                className={clsx(styles.tocItem, index === selectedSectionIndex && styles.tocItemActive)}
                onClick={() => setSelectedSectionIndex(index)}
                type="button"
              >
                <span>{section.name}</span>
              </button>
            ))}
          </nav>
        </aside>
        <div className={styles.contentColumn} ref={contentColumnRef}>
          <label className={styles.mobileSectionPicker} htmlFor="dynamed-section-select">
            <span className={styles.mobileSectionPickerLabel}>Section</span>
            <select
              className={styles.mobileSectionSelect}
              id="dynamed-section-select"
              onChange={(event) => setSelectedSectionIndex(Number(event.target.value))}
              value={selectedSectionIndex}
            >
              {sections.map((section, index) => (
                <option key={`${section.name}-${index}`} value={index}>
                  {section.name}
                </option>
              ))}
            </select>
          </label>
          <SectionNavigation
            currentSectionName={currentSection.name}
            label={label}
            nextSectionName={nextSection?.name}
            onNextSection={() => nextSection && setSelectedSectionIndex(selectedSectionIndex + 1)}
            onPreviousSection={() => previousSection && setSelectedSectionIndex(selectedSectionIndex - 1)}
            position="top"
            previousSectionName={previousSection?.name}
            sectionCount={sections.length}
            selectedSectionIndex={selectedSectionIndex}
            isHeader
          />
          <div className={styles.sectionContentFade} key={currentSection.name}>
            <RenderedHtmlWithPopup
              className={clsx(chatStyles.pageContent, styles.dynamedContent)}
              content={transformedContent.content}
              creatorMode={!!allowEditing}
              documentId={documentId}
              documentSlug={documentSlug}
              documentVersionId={documentVersionId}
              onClick={handleContentClick}
              pageId={pageId}
              pageName={`${label}: ${currentSection.name}`}
              popupRules={[
                {
                  selector: 'button[data-auto="button"].trigger',
                  getPopupHtml: (element) => {
                    const recommendationLabel = element.textContent?.replace(/\[|\]/g, '').trim();
                    return recommendationLabel
                      ? DYNAMED_RECOMMENDATION_TOOLTIP_BY_LABEL[recommendationLabel]
                      : undefined;
                  },
                },
              ]}
            />
          </div>
          <SectionNavigation
            currentSectionName={currentSection.name}
            label={label}
            nextSectionName={nextSection?.name}
            onNextSection={() => nextSection && setSelectedSectionIndex(selectedSectionIndex + 1)}
            onPreviousSection={() => previousSection && setSelectedSectionIndex(selectedSectionIndex - 1)}
            position="bottom"
            previousSectionName={previousSection?.name}
            sectionCount={sections.length}
            selectedSectionIndex={selectedSectionIndex}
          />
        </div>
      </div>
      <Dialog fullScreen open={!!selectedTable} onClose={() => setSelectedTableId(null)}>
        <DialogTitle className={styles.tableDialogTitle}>
          <TableChartOutlinedIcon aria-hidden="true" />
          <span>{selectedTable?.title}</span>
          <IconButton aria-label="Close table" onClick={() => setSelectedTableId(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={styles.tableDialogContent}>
          {selectedTable ? (
            <RenderedHtmlWithPopup
              className={clsx(chatStyles.pageContent, styles.dynamedContent)}
              content={selectedTable.content}
              creatorMode={!!allowEditing}
              documentId={documentId}
              documentSlug={documentSlug}
              documentVersionId={documentVersionId}
              onClick={handleContentClick}
              pageId={pageId}
              pageName={`${label}: ${selectedTable.title}`}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
